import {
  convertEmailAddress,
  convertPaxName,
  convertPhoneNumber,
  convertReservationInfo,
  convertToHyphen,
} from '@lib/helpers';
import { GenderCodeType } from '@lib/interfaces';

/**
 * 表示用文言取得
 * @returns 表示用文言
 */
export function getNameStaticLabel(genderCode: string) {
  switch (genderCode) {
    case GenderCodeType.MALE:
      return 'label.male.title';
    case GenderCodeType.FEMALE:
      return 'label.female.title';
    default:
      return 'label.unknown.title';
  }
}

/**
 * 文字列が空文字もしくはundefined or nullの場合true
 * @param srt 文字列
 * @returns true\false
 */
export function isStringEmpty(str?: string): boolean {
  if (str === undefined || str === null || str === '') {
    return true;
  }
  return false;
}

/**
 * 数値はゼロ、NULL、未定義です
 * @param value
 * @returns true/false
 */
export function isNumberZeroNullUndefined(value?: number): boolean {
  return value === 0 || value === undefined || value === null;
}

/**
 * 文字列が空文字もしくはundefined or nullの場合デフォルト文字列を返す
 * @param srt 文字列
 * @param def デフォルト文字列
 * @returns 文字列
 */
export function stringEmptyDefault(str?: string, def: string = ''): string {
  return isStringEmpty(str) ? def : str ?? '';
}

/**
 * 文字列が空文字もしくはundefined or nullの場合undefinedを返す
 * @param srt 文字列
 * @returns 文字列
 */
export function stringEmptyUndefined(str?: string): string | undefined {
  return isStringEmpty(str) ? undefined : str ?? '';
}

/**
 * 0の場合undefinedを返す
 * @param num 値
 * @returns 値
 */
export function numberZeroUndefined(num?: number): number | undefined {
  return num === 0 ? undefined : num;
}

/**
 * オブジェクトのから判定
 * @param オブジェクト
 * @returns true/false
 */
export function isEmptyObject(obj?: object): boolean {
  return obj === undefined || obj === null || Object.keys(obj).length === 0;
}

export function isEmptyAllItemObject(obj?: object): boolean {
  if (isEmptyObject(obj)) {
    return true;
  }
  return Object.values(obj as any).every((item: any) => {
    if (typeof item === 'object') {
      return isEmptyAllItemObject(item);
    }
    if (isStringEmpty(item)) {
      return true;
    }
    return false;
  });
}

/**
 * Function check empty array
 * @param arr Array
 * @returns true/false
 */
export function isEmptyArray<T>(arr?: Array<T>) {
  return arr === null || arr === undefined || (Array.isArray(arr) && arr.length === 0);
}

/**
 * お金を日本語に変換
 * @param 額
 * @returns お金
 */
export function convertMoneyToJapanese(amount?: number): string {
  const formattedAmount = amount?.toLocaleString('ja-JP', {
    style: 'currency',
    currency: 'JPY',
  });

  return formattedAmount ?? '';
}

/**
 * 配列を変換する
 * @param Array
 * @returns 配列
 */
export function parseEmptyArray<T>(arr?: T[]) {
  return Array.isArray(arr) ? arr : [];
}

/**
 * Convert Date to yyyymmdd
 * @param date
 * @returns yyyymmdd
 */
export const convertDateToString = (date: Date | null) => {
  if (date) {
    let mm = date.getMonth() + 1; // getMonth() is zero-based
    let dd = date.getDate();
    const yyyymmdd = [date.getFullYear(), (mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd].join('');
    return yyyymmdd;
  }
  return '';
};

/**
 * Convert string yyyymmdd to Date
 * @param yyyymmdd
 * @returns Date
 */
export const convertStringToDate = (yyyymmdd: string) => {
  const year = yyyymmdd.substring(0, 4);
  const month = yyyymmdd.substring(4, 6);
  const day = yyyymmdd.substring(6, 8);
  const stringDate = year + '-' + month + '-' + day;
  return new Date(stringDate);
};

export const convertFullsizeToHalfSize = (
  value: any,
  type: 'reserveInfo' | 'paxName' | 'phoneNumber' | 'hyphen' | 'email' | 'common' = 'common'
) => {
  let convertValue = '';
  switch (type) {
    case 'common':
      convertValue = value;
      break;
    // 予約情報変換
    case 'reserveInfo':
      convertValue = convertReservationInfo(value);
      break;
    // 搭乗者名変換
    case 'paxName':
      convertValue = convertPaxName(value);
      break;
    // 電話番号変換
    case 'phoneNumber':
      convertValue = convertPhoneNumber(value);
      break;
    // ハイフン変換
    case 'hyphen':
      convertValue = convertToHyphen(value);
      break;
    // メールアドレス変換
    case 'email':
      convertValue = convertEmailAddress(value);
      break;
    default:
      convertValue = value;
  }
  return convertValue;
};

/**
 * Formats a given number of seconds into a string in the format HH:MM:SS.
 *
 * @param {number | undefined} sec - The number of seconds to format. Can be undefined.
 * @returns {string} - A string representing the formatted time in HH:MM:SS format.
 *                     Returns an empty string if the input is undefined.
 */
export function formatSeconds(sec: number | undefined): string {
  if (sec === undefined || typeof sec === 'undefined') {
    return '';
  }

  const hours = zeroPadding(Math.floor(sec / 3600), 2);
  const minutes = zeroPadding(Math.floor((sec % 3600) / 60), 2);
  const seconds = zeroPadding(Math.floor(sec % 60), 2);

  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Pads a given number with leading zeros to ensure it has at least the specified number of digits.
 *
 * @param {number} num - The number to be padded with zeros.
 * @param {number} digits - The minimum number of digits the output string should have.
 * @returns {string} - A string representation of the number, padded with leading zeros.
 */
export function zeroPadding(num: number, digits: number): string {
  let result = num.toString();

  for (let i = digits; i > 1; i--) {
    if (num < 10 ** (i - 1)) {
      result = '0' + result;
    }
  }

  return result;
}
